/**
 * Email Account Card
 * Displays a single email account with status and actions
 */
import { useTranslation } from "react-i18next";
import {
    Mail01,
    Settings01,
    Trash01,
    RefreshCw01,
    AlertCircle,
    InfoCircle,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import type { EmailAccountResponse, EmailAccountStatus } from "@/types/api/email";
import { cx } from "@/utils/cx";

interface EmailAccountCardProps {
    account: EmailAccountResponse;
    onConfigure: (account: EmailAccountResponse) => void;
    onRemove: (account: EmailAccountResponse) => void;
    onReconnect: (account: EmailAccountResponse) => void;
    onSync: (account: EmailAccountResponse) => void;
    onToggleSync: (account: EmailAccountResponse, enabled: boolean) => void;
    onStopSync?: (account: EmailAccountResponse) => void;
    isSyncing?: boolean;
    isToggling?: boolean;
}

const providerIcons: Record<string, string> = {
    gmail: "/icons/gmail.svg",
    outlook: "/icons/outlook.svg",
    imap_smtp: "/icons/email.svg",
};

const statusBadgeColors: Record<EmailAccountStatus, BadgeColors> = {
    active: "success",
    inactive: "gray",
    error: "error",
    authentication_required: "warning",
    syncing: "gray",
};

function formatLastSync(lastSyncAt: string | null, t: (key: string, options?: Record<string, unknown>) => string): string {
    if (!lastSyncAt) {
        return t("accountsList.neverSynced");
    }

    const syncDate = new Date(lastSyncAt);
    const now = new Date();
    const diffMs = now.getTime() - syncDate.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    // Less than 1 minute - show "just now"
    if (diffSeconds < 60) {
        return t("accountsList.justNow");
    } else if (diffMinutes < 60) {
        return t("accountsList.minutesAgo", { count: diffMinutes });
    } else if (diffHours < 24) {
        return t("accountsList.hoursAgo", { count: diffHours });
    } else {
        return syncDate.toLocaleDateString();
    }
}

export const EmailAccountCard = ({
    account,
    onConfigure,
    onRemove,
    onReconnect,
    onSync,
    onToggleSync,
    onStopSync,
    isSyncing = false,
    isToggling = false,
}: EmailAccountCardProps) => {
    const { t } = useTranslation(["pages:email-settings"]);

    const badgeColor = statusBadgeColors[account.status] || "gray";
    const needsReconnect = account.status === "authentication_required" || account.status === "error";
    const isSyncEnabled = account.status === "active";
    const needsConfiguration = !account.sync_from_date;
    const isSyncingStatus = account.status === "syncing";

    return (
        <div className="rounded-xl bg-secondary p-4 shadow-xs ring-1 ring-tertiary transition-shadow hover:shadow-md">
            <div className="flex items-start justify-between gap-4">
                {/* Left: Provider icon and account info */}
                <div className="flex items-start gap-3">
                    {/* Provider Icon */}
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-tertiary">
                        {providerIcons[account.provider] ? (
                            <img
                                src={providerIcons[account.provider]}
                                alt={account.provider}
                                className="h-6 w-6"
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.parentElement?.querySelector(".fallback-icon")?.classList.remove("hidden");
                                }}
                            />
                        ) : null}
                        <Mail01 className={cx("h-6 w-6 text-quaternary", providerIcons[account.provider] ? "hidden fallback-icon" : "")} />
                    </div>

                    {/* Account Info */}
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-primary">
                                {account.display_name || account.email_address}
                            </span>
                            {/* Status Badge */}
                            <BadgeWithDot color={badgeColor} size="sm">
                                {t(`status.${account.status}`)}
                            </BadgeWithDot>
                        </div>
                        {account.display_name && (
                            <span className="text-sm text-secondary">
                                {account.email_address}
                            </span>
                        )}
                        <div className="mt-1 flex items-center gap-2 text-xs text-tertiary">
                            <span>{t(`providers.${account.provider}`)}</span>
                            <span>•</span>
                            <span>
                                {t("accountsList.lastSync")}:{" "}
                                {isSyncingStatus ? t("accountsList.syncInProgress") : formatLastSync(account.last_sync_at, t)}
                            </span>
                        </div>
                        {needsConfiguration && (
                            <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-warning-600">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                <span>{t("accountsList.configRequired")}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-2">
                    {isSyncingStatus ? (
                        <>
                            <span className="text-sm text-tertiary">{t("accountsList.syncing")}</span>
                            <Tooltip
                                title={t("accountsList.syncingInfoTooltip")}
                                placement="top"
                                closeDelay={400}
                                delay={150}
                            >
                                <TooltipTrigger
                                    className="rounded p-1 text-tertiary hover:text-primary focus:outline-none focus:ring-2 focus:ring-focus-ring"
                                    aria-label={t("accountsList.syncingInfoTooltip")}
                                >
                                    <InfoCircle className="h-5 w-5" />
                                </TooltipTrigger>
                            </Tooltip>
                            <Button
                                onClick={() => onStopSync?.(account)}
                                color="secondary"
                                size="sm"
                            >
                                {t("accountsList.stopSync")}
                            </Button>
                        </>
                    ) : (
                        <>
                            {/* Sync Button */}
                            <Button
                                onClick={() => onSync(account)}
                                isDisabled={isSyncing || needsReconnect || !isSyncEnabled || needsConfiguration}
                                isLoading={isSyncing}
                                color="secondary"
                                size="sm"
                                iconLeading={RefreshCw01}
                            >
                                {isSyncing ? t("accountsList.syncing") : t("accountsList.syncNow")}
                            </Button>

                            {/* Sync Toggle */}
                            <Toggle
                                size="sm"
                                isSelected={isSyncEnabled}
                                isDisabled={needsReconnect || isToggling || needsConfiguration || isSyncing}
                                onChange={(enabled) => onToggleSync(account, enabled)}
                                aria-label={t("accountsList.toggleSync")}
                            />

                            {/* Reconnect Button (only shown when needed) */}
                            {needsReconnect && (
                                <Button
                                    onClick={() => onReconnect(account)}
                                    color="secondary"
                                    size="sm"
                                    iconLeading={RefreshCw01}
                                >
                                    {t("actions.reconnect")}
                                </Button>
                            )}

                            {/* Configure Button */}
                            <div className="relative overflow-visible">
                                <Button
                                    onClick={() => onConfigure(account)}
                                    color="tertiary"
                                    size="sm"
                                    iconLeading={Settings01}
                                    aria-label={t("actions.configure")}
                                    isDisabled={isSyncing}
                                />
                                {/* Red dot: needs initial sync configuration */}
                                {needsConfiguration && (
                                    <span className="pointer-events-none absolute -top-1 -right-1 z-10 flex h-3 w-3">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error opacity-75" />
                                        <span className="relative inline-flex h-3 w-3 rounded-full bg-error" />
                                    </span>
                                )}
                            </div>

                            {/* Remove Button */}
                            <Button
                                onClick={() => onRemove(account)}
                                color="tertiary-destructive"
                                size="sm"
                                iconLeading={Trash01}
                                aria-label={t("actions.remove")}
                                isDisabled={isSyncing}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
